/* eslint-disable ember/classic-decorator-hooks, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-runloop, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import LogSeverity from '@linc-technologies/linced-common/enums/process-monitor-log-severity';
import ProcessMonitorStatus from '@linc-technologies/linced-common/enums/process-monitor-status';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import config from '@linc-technologies/linced-common/utils/config';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { later } from '@ember/runloop';

export default class ProcessMonitorViewer extends Component {
  LogSeverity = LogSeverity;
  ProcessMonitorStatus = ProcessMonitorStatus;

  // Services
  @service
  fetch;

  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  toaster;

  @tracked
  errorPanelOpen = false;
  initialMonitorId = '';
  @tracked
  monitorPanelOpen = false;
  openLatest = false;
  syncButtonsEnabled = false;
  syncEnabled = false;
  dirty = false;
  title = 'Previous Syncs';
  @tracked isFirstLoad = true;
  @tracked
  processMonitors = [];
  @tracked
  selected = null;
  schoolId = '';
  taskName = '';
  @tracked
  resourceIds = [];
  @tracked
  completedSelected = false;
  progressBarOnly = false;
  onPollingResult = null;
  @tracked
  performPostPoll = false;

  parseError(error) {
    let matches = error.message.match(/([A-Z][a-zA-Z]+):\s*\[([^\]]+)]/g);

    let info = {
      severity: error.severity,
      message: error.message,
    };

    if (matches) {
      matches.forEach((match) => {
        let [key, value] = match.split(/:\s*\[|]/).filter(Boolean);
        info[key.toLowerCase()] = value;
      });
      info['message'] = error.message.split(matches[0])[0];
    }

    return info;
  }

  @computed('selected.errors.[]')
  get formattedErrors() {
    return this.selected.errors
      .map((error) => {
        return this.parseError(error);
      })
      .filter(Boolean);
  }

  @computed('selected.errors.@each.severity')
  get logCountsBySeverity() {
    let counts = this.selected.errors.reduce((counts, entry) => {
      if (isEmpty(counts[entry.severity])) {
        counts[entry.severity] = 0;
      }
      counts[entry.severity]++;
      return counts;
    }, {});
    return Object.entries(counts)
      .map(([severity, count]) => {
        return `${LogSeverity.getLabel(parseInt(severity, 10))}: ${count}`;
      })
      .join(', ');
  }

  @computed('session.scopes.[]')
  get isHeroAdmin() {
    return this.permissions.meet(Scopes.LINCED_ADMIN);
  }

  updateSelectedProcessMonitor() {
    if (isEmpty(this.selected)) {
      return;
    }

    if (this.selected.status === ProcessMonitorStatus.COMPLETE) {
      if (!isEmpty(this.selected.resourceIds)) {
        // In case we want to do something with the polling results (i.e. PDFs returned from a long task)
        this.resourceIds = this.selected.resourceIds;
      }
      if (this.performPostPoll) {
        if (typeof this.onPollingResult === 'function') {
          this.onPollingResult(this.selected.resourceIds);
          this.performPostPoll = false;
        }
        return;
      }

      this.completedSelected = true;
      return;
    }

    this.store
      .findRecord(`cron/process-monitor`, this.selected.id)
      .then((monitor) => {
        this.selected = monitor;
      });
  }

  pollSelectedProcessMonitor() {
    if (!isEmpty(this.selected)) {
      this.updateSelectedProcessMonitor();
      later(
        this,
        function () {
          this.pollSelectedProcessMonitor();
        },
        1000,
      );
    }
  }

  refreshAndSelectLatestMonitor(selectLatest) {
    this.lilRefresh().then(() => {
      if (this.processMonitors.length <= 0) {
        return;
      }
      if (selectLatest) {
        let selected = this.processMonitors[0];
        this.monitorPanelOpen = true;
        this.selected = selected;
        this.performPostPoll =
          selected.status !== ProcessMonitorStatus.COMPLETE;
        this.completedSelected =
          selected.status === ProcessMonitorStatus.COMPLETE;

        this.pollSelectedProcessMonitor();
        return;
      }
      if (!isEmpty(this.initialMonitorId)) {
        let selected = this.processMonitors.find(
          (monitor) => monitor.id === this.initialMonitorId,
        );
        // This was the first load, so any subsequent load is not.
        this.isFirstLoad = false;
        // Select the requested monitor, if it was in the list.
        this.selected = selected;
        // Open the monitor panel, if we found it.
        this.monitorPanelOpen = !isEmpty(selected);
        this.performPostPoll =
          selected.status !== ProcessMonitorStatus.COMPLETE;
        this.completedSelected =
          selected.status === ProcessMonitorStatus.COMPLETE;
        this.pollSelectedProcessMonitor();
        return;
      }
      this.pollSelectedProcessMonitor();
    });
  }

  // he's a wrapper
  lilRefresh() {
    return this.store
      .query('cron/process-monitor', {
        schoolId:
          this.schoolId === '' ? this.session.activeSchool : this.schoolId,
        name: encodeURIComponent(this.taskName),
      })
      .then((monitors) => {
        let sorted = monitors
          .slice()
          .sort((a, b) =>
            a.createtime > b.createtime
              ? 1
              : a.createtime < b.createtime
                ? -1
                : 0,
          );
        this.processMonitors = sorted.reverse();
      });
  }

  init() {
    super.init(...arguments);
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    this.refreshAndSelectLatestMonitor(this.openLatest);
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    this.refreshAndSelectLatestMonitor();
  }

  willDestroyElement() {
    this.selected = null;
    super.willDestroyElement(...arguments);
  }

  @action
  startSync(isDryRun) {
    let url = `${config.APP.apiHost}/providers/wonde/${config.APP.apiPath}/sync:start`;
    let params = {
      schoolId: this.session.activeSchool,
      isDryRun: isDryRun,
    };
    return this.fetch
      .post(url, { data: JSON.stringify(params) })
      .then(() => {
        this.refreshAndSelectLatestMonitor(true);
      })
      .catch(() => {
        this.toaster.error(`Error synchronising students!`);
      });
  }

  @action
  viewSyncDetails(selected) {
    if (this.selected === selected) {
      return;
    }
    this.monitorPanelOpen = true;
    this.selected = selected;
    this.errorPanelOpen = false;
    this.completedSelected = false;
    this.performPostPoll = selected.status !== ProcessMonitorStatus.COMPLETE;
    this.pollSelectedProcessMonitor();
  }

  @action
  viewErrors() {
    this.errorPanelOpen = true;
  }

  @action
  refresh() {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    this.lilRefresh();
  }

  @action
  copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
      this.toaster.success('Copied to clipboard');
    });
  }
}
